import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import { baseAPI } from '../../../../config';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import ExpedientsFilters from '../ExpedientsFilters';
import Pagination from '../../../../components/Pagination';
import userStore from '../../../../store/userStore';
import ExpedientRow from './components/ExpedientRow';
import BulkDeactivateBar from './components/BulkDeactivateBar';
import BulkAssignBar from './components/BulkAssignBar';
import api from '../../../../services/api';
import { isGroupUser } from '../../../../utils/userScope';
import { EXPEDIENT_TYPES, STATUSES_WITH_ACTIVE_MANAGEMENT } from '@claimup/shared/constants/expedient';

/**
 * ¿Se puede marcar esta fila para la baja masiva? Solo expedientes AR en
 * estado activo: lo demás no se gestiona como AR o ya está fuera de gestión.
 * Quién puede lo decide el servidor; aquí solo se ahorra ofrecer lo imposible.
 */
export const isDeactivatable = expedient =>
  expedient?.type === EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE && STATUSES_WITH_ACTIVE_MANAGEMENT.includes(expedient?.estado);

/** Los que pueden dar de baja en masivo: los mismos que escalan a recobro. */
const BULK_ROLES = ['admin', 'company', 'gestor'];

/**
 * Qué filas se pueden marcar: las AR en gestión, y para el admin también las de
 * recobro, que puede devolver a AR como corrección. La empresa que reparte su
 * equipo, todas: asignar vale para cualquier expediente suyo (la baja y el paso
 * a recobro saltan, y lo dicen, los que no son AR en gestión).
 */
export const isSelectable = (expedient, isAdmin, canAssignTeam = false) =>
  canAssignTeam ||
  isDeactivatable(expedient) ||
  (isAdmin && (expedient?.type || EXPEDIENT_TYPES.EXTRA_JUDICIAL) === EXPEDIENT_TYPES.EXTRA_JUDICIAL);

/**
 * ¿Reparte esta persona expedientes entre su equipo? La empresa, salvo si todo
 * su equipo ve todos los expedientes (entonces no se mira a quién están
 * asignados) o si es un usuario de grupo, que es de solo lectura.
 */
export const canAssignTeam = user =>
  user?.role === 'company' && !user?.operators_handle_all && !isGroupUser(user);

const ExpedientsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expedients, setExpedients] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 20;
  const [showFilter, setShowFilter] = useState(false);
  const [sortField] = useState('importe_deuda');
  // La columna y el filtro «Fase» solo donde hay AR: lo dice el servidor.
  const [showPhases, setShowPhases] = useState(false);
  const { user } = userStore();
  // Baja masiva: lo marcado, y un contador para recargar la lista al terminar.
  const [selected, setSelected] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const bulkMode = BULK_ROLES.includes(user?.role) && !isGroupUser(user);
  const isAdmin = user?.role === 'admin';
  // Repartir el equipo en bloque y filtrar por persona (docs/asignar-equipo-en-bloque-plan.md).
  const reparte = canAssignTeam(user);
  const [team, setTeam] = useState([]);
  const marcables = (expedients || []).filter(e => isSelectable(e, isAdmin, reparte)).map(e => String(e._id));
  const toggle = id => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const todosMarcados = marcables.length > 0 && marcables.every(id => selected.includes(id));
  const toggleTodos = () =>
    setSelected(s => (todosMarcados ? s.filter(id => !marcables.includes(id)) : [...new Set([...s, ...marcables])]));

  // Get values from URL params
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const searchText = searchParams.get('search') || '';
  const selectedEstado = searchParams.get('estado') || '';
  const selectedPhase = searchParams.get('fase') || '';
  const selectedUser = searchParams.get('cliente') || '';
  // Vacío porque no hay nada o porque los filtros lo esconden: dicen cosas distintas.
  const hayFiltros = [...searchParams.keys()].some(k => k !== 'page' && searchParams.get(k));
  const puedeImportar = ['admin', 'company'].includes(user?.role) && !isGroupUser(user);
  const selectedTeamMember = searchParams.get('team_member') || '';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  const startDateParam = searchParams.get('startDate') || '';
  const endDateParam = searchParams.get('endDate') || '';
  const startDate = useMemo(() => (startDateParam ? new Date(startDateParam) : null), [startDateParam]);
  const endDate = useMemo(() => (endDateParam ? new Date(endDateParam) : null), [endDateParam]);

  // Helper function to update URL params
  const updateUrlParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== 'page') {
      newParams.delete('page'); // Reset to first page when filters change
    }
    setSearchParams(newParams);
  };

  const exportToExcel = async () => {
    try {
      const userToken = localStorage.getItem('token');
      if (!userToken) {
        toast.error('No estás autenticado.', {
          position: 'top-right',
          autoClose: 5000,
        });
        return;
      }

      const params = {
        estado: selectedEstado,
        type: selectedPhase || undefined,
        cliente: selectedUser,
        team_member: selectedTeamMember || undefined,
        search: searchText,
        sortField,
        sortOrder,
        startDate: startDateParam || null,
        endDate: endDateParam || null,
      };

      const response = await axios.get(`${baseAPI}/expedients/export`, {
        params,
        responseType: 'blob',
        headers: { Authorization: `Bearer ${userToken}` },
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, 'expedients.xlsx');

      toast.success('Exportación exitosa!', {
        position: 'top-right',
        autoClose: 5000,
      });
    } catch (error) {
      console.error(error);
      toast.error('Error al exportar los expedientes.', {
        position: 'top-right',
        autoClose: 5000,
      });
    }
  };

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      updateUrlParam('page', newPage.toString());
    }
  };

  const toggleSortOrder = () => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    updateUrlParam('sortOrder', newSortOrder);
  };

  const setSelectedEstado = value => updateUrlParam('estado', value);
  const setSelectedPhase = value => updateUrlParam('fase', value);
  const setSelectedUser = value => updateUrlParam('cliente', value);
  const setSelectedTeamMember = value => updateUrlParam('team_member', value);

  // La gente de la empresa a la que se le puede asignar: operadores y gestores.
  useEffect(() => {
    if (!reparte) return;
    api
      .get('/users')
      .then(({ data }) => setTeam((data || []).filter(u => ['operator', 'gestor'].includes(u.userrole))))
      .catch(() => setTeam([]));
  }, [reparte]);
  const setStartDate = value => updateUrlParam('startDate', value ? moment(value).format('YYYY-MM-DD') : '');
  const setEndDate = value => updateUrlParam('endDate', value ? moment(value).format('YYYY-MM-DD') : '');

  useEffect(() => {
    // Se lanza una petición por tecla. Sin esto, la respuesta de «mar» podía
    // llegar después de la de «maria» y dejar en pantalla un resultado viejo.
    let vigente = true;
    const fetchExpedients = async () => {
      const userToken = localStorage.getItem('token');
      if (!userToken) {
        console.log('no hay token');
        return;
      }

      try {
        const params = {
          page: currentPage,
          limit: itemsPerPage,
          search: searchText,
          estado: selectedEstado,
          type: selectedPhase || undefined,
          cliente: selectedUser,
          team_member: selectedTeamMember || undefined,
          sortField,
          sortOrder,
          startDate: startDateParam || null,
          endDate: endDateParam || null,
        };

        const response = await axios.get(`${baseAPI}/expedients`, {
          params,
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (!vigente) return;
        setExpedients(response.data.expedients);
        setTotalPages(response.data.totalPages);
        setShowPhases(!!response.data.showPhases);
        setSelected([]);
      } catch (error) {
        console.log(error);
      }
    };

    fetchExpedients();
    return () => {
      vigente = false;
    };
  }, [
    currentPage,
    searchText,
    selectedEstado,
    selectedPhase,
    selectedUser,
    selectedTeamMember,
    itemsPerPage,
    sortField,
    sortOrder,
    startDateParam,
    endDateParam,
    reloadKey,
  ]);

  return (
    <div className='col-span-full xl:col-span-12 bg-white shadow-lg rounded-lg border border-gray-200'>
      <header className='px-5 py-4 border-b border-slate-100'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0'>
          <h2 className='font-semibold text-slate-800'>
            <div className='flex items-center'>
              <svg className='w-5 h-5 mr-2' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              Reclamaciones
            </div>
          </h2>
          <div className='flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3'>
            <button
              onClick={() => setShowFilter(!showFilter)}
              className='inline-flex items-center w-full sm:w-auto justify-center px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150'
            >
              <svg className='w-4 h-4 mr-2' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z'
                />
              </svg>
              {showFilter ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </button>
            <button
              onClick={exportToExcel}
              className='inline-flex items-center w-full sm:w-auto justify-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150'
            >
              <svg className='w-4 h-4 mr-2' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                />
              </svg>
              Exportar
            </button>
          </div>
        </div>
      </header>

      <div className='p-5'>
        <ExpedientsFilters
          showFilter={showFilter}
          selectedEstado={selectedEstado}
          setSelectedEstado={setSelectedEstado}
          showPhases={showPhases}
          selectedPhase={selectedPhase}
          setSelectedPhase={setSelectedPhase}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          role={user?.role}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          team={reparte ? team : null}
          selectedTeamMember={selectedTeamMember}
          setSelectedTeamMember={setSelectedTeamMember}
        />

        {reparte && (
          <BulkAssignBar selectedIds={selected} team={team} onDone={() => setReloadKey(k => k + 1)} />
        )}

        {bulkMode && (
          <BulkDeactivateBar
            selectedIds={selected}
            isAdmin={isAdmin}
            onClear={() => setSelected([])}
            onDone={() => setReloadKey(k => k + 1)}
          />
        )}

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead>
              <tr className='bg-gray-50'>
                {bulkMode && (
                  <th className='px-4 py-3 text-left'>
                    <input
                      type='checkbox'
                      aria-label='Marcar todos los de esta página'
                      checked={todosMarcados}
                      disabled={marcables.length === 0}
                      onChange={toggleTodos}
                    />
                  </th>
                )}
                {showPhases && (
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Fase
                  </th>
                )}
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Fecha Creación
                </th>
                {(user?.role === 'admin' || isGroupUser(user)) && (
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    {user?.role === 'admin' ? 'Cliente' : 'Empresa'}
                  </th>
                )}
                {(user?.role === 'admin' || (user?.role === 'company' && !user?.operators_handle_all)) && (
                  <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    Operador
                  </th>
                )}
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Deudor
                </th>
                <th
                  onClick={toggleSortOrder}
                  className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700'
                >
                  <div className='flex items-center'>
                    Importe Deuda
                    <svg
                      className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${
                        sortOrder === 'asc' ? 'rotate-180' : ''
                      }`}
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 15l7-7 7 7' />
                    </svg>
                  </div>
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Estado
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Días
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {expedients?.map(expedient => (
                <ExpedientRow
                  key={expedient._id}
                  createdAt={expedient.createdAt}
                  expedient={expedient}
                  role={user?.role}
                  showPhase={showPhases}
                  bulkMode={bulkMode}
                  selectable={isSelectable(expedient, isAdmin, reparte)}
                  selected={selected.includes(String(expedient._id))}
                  onToggle={() => toggle(String(expedient._id))}
                />
              ))}
            </tbody>
          </table>

          {expedients?.length === 0 && (
            <div className='text-center py-12'>
              <svg className='mx-auto h-12 w-12 text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                />
              </svg>
              {hayFiltros ? (
                <>
                  <h3 className='mt-2 text-sm font-medium text-gray-900'>Ningún expediente con estos filtros</h3>
                  <p className='mt-1 text-sm text-gray-500'>Prueba a quitar alguno.</p>
                </>
              ) : (
                <>
                  <h3 className='mt-2 text-sm font-medium text-gray-900'>Aún no hay expedientes</h3>
                  {puedeImportar ? (
                    <Link
                      to='/import-expedients'
                      className='mt-3 inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'
                    >
                      Importa tu primer Excel
                    </Link>
                  ) : (
                    <p className='mt-1 text-sm text-gray-500'>Cuando tu empresa los importe, aparecerán aquí.</p>
                  )}
                </>
              )}
            </div>
          )}

          {totalPages > 1 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpedientsList;
